import type { HandlingTimeBucket } from './handlingTime.api';
import { STAP_VALIDEREN, STAP_VASTLEGGEN } from './kerncijfers';

/**
 * Story 31.3 — de reeks over de tijd, vóór er iets getekend wordt.
 *
 * Hier zit het zwaartepunt van deze story, en het is met opzet tegenintuïtief: **een tijdvak zonder
 * meting ONTBREEKT in het antwoord van de bron**. Twee metingen met een lege week ertussen leveren
 * twee regels en niet negen. Zou het scherm dat één op één tekenen, dan schuiven die twee metingen
 * tegen elkaar aan en leest de grafiek alsof de dagen ertussen niet bestonden.
 *
 * Dit bestand vouwt daarom de volledige kalender van de gekozen periode uit en hángt de metingen
 * daaraan op. Wat overblijft is een tijdvak dat er wél is maar niets draagt: `gemeten: false`, met
 * overal `null` en nergens een nul. De tekening erboven hoeft dan alleen nog te tekenen wat hier al
 * vaststaat — geen staaf, een gat in de lijn, en tóch zichtbaar als "niet gemeten".
 *
 * Alles staat hier in gewoon rekenwerk en niet in de tekening, want een rekenregel is te toetsen en
 * een pixel niet.
 */

/** De drie tijdvakken die de bron kent. Dezelfde namen, want ze gaan ongewijzigd de vraag in. */
export type Korrel = 'day' | 'week' | 'month';

/**
 * Tot en met zoveel dagen blijft de korrel per dag.
 *
 * Precies 31 dagen is nog per dag: dat is een volle maand, en die hoort niet per week te
 * verschijnen omdat hij één dag langer is dan februari.
 */
export const KORREL_GRENS_PER_DAG = 31;

/**
 * Tot en met dit aantal dagen blijft de korrel per week; daarboven per maand.
 *
 * HONDERDDRIEËNTACHTIG DAGEN, en dat is met opzet een vast getal en niet "een half jaar". Een
 * kalenderhalfjaar bestaat niet in één lengte: 1 januari t/m 30 juni is 181 dagen, 1 juli t/m
 * 31 december is er 184. Zou de grens de kalender volgen, dan viel dezelfde vraag in de ene helft
 * van het jaar per week en in de andere per maand — en dan verspringt een grafiek van vorm zonder
 * dat iemand iets veranderd heeft.
 *
 * Het gevolg dat we aanvaarden: de tweede jaarhelft (184 dagen) valt nét in de maandkorrel. Dat is
 * één dag verschil op een grens die toch een keuze is, en de korrel staat zichtbaar op het scherm.
 * De uitleg dáár noemt daarom het dagental en niet "een half jaar" (reviewbevinding 27-08-2026).
 */
export const KORREL_GRENS_PER_WEEK = 183;

export interface Periode {
  from: string;
  to: string;
}

export interface StapInTijdvak {
  /** Mensminuten in deze stap. `null` — nooit `0` — zodra er niets gemeten is. */
  minuten: number | null;
  /** Het aantal behandelingen in DEZE stap; de noemer onder het gemiddelde ernaast. */
  behandelingen: number | null;
  /** Minuten van deze stap gedeeld door de behandelingen van díe stap. */
  gemiddeldePerStuk: number | null;
}

export interface Tijdvak {
  /** De sleutel zoals de bron hem noemt: `2026-08-24` bij dag en week, `2026-08` bij maand. */
  label: string;
  /** De eerste kalenderdag van het tijdvak. */
  van: string;
  /** De laatste kalenderdag van het tijdvak, MEEGETELD. */
  tot: string;
  korrel: Korrel;
  /**
   * Er is in dit tijdvak werkelijk gemeten.
   *
   * `false` betekent geen staaf en een gat in de lijn — nadrukkelijk niet een staaf van nul en niet
   * een onzichtbare staaf.
   */
  gemeten: boolean;
  /**
   * Het tijdvak steekt over de periodegrens heen.
   *
   * Vraag je een week op die op woensdag begint, dan loopt het eerste tijdvak vanaf de maandag
   * ervóór. Dat wordt NIET afgekapt — de bron vouwt over de Nederlandse kalender — maar het scherm
   * mag zo'n tijdvak ook niet als een volle week presenteren. De minuten erin zijn wél uitsluitend
   * die uit de gevraagde periode.
   */
  rand: boolean;
  /** De som over de twee stappen. `null` zodra er niets gemeten is. */
  totaleMinuten: number | null;
  vastleggen: StapInTijdvak;
  valideren: StapInTijdvak;
}

/** Een kale datum als lokale kalenderdag; nooit via `new Date(string)`, dat leest UTC. */
function parse(datum: string): Date {
  const [jaar, maand, dag] = datum.slice(0, 10).split('-').map(Number);
  return new Date(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1);
}

function alsDatum(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Dagen optellen via de kalendervelden en niet in milliseconden.
 *
 * Een etmaal is twee keer per jaar 23 of 25 uur lang; in milliseconden rekenen komt dan op de
 * verkeerde dag uit.
 */
function verschuif(d: Date, dagen: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dagen);
}

/** De maandag van de week waarin deze dag valt. Zondag hoort bij de week die eraan voorafging. */
function maandagVan(d: Date): Date {
  const dagNummer = d.getDay();
  return verschuif(d, dagNummer === 0 ? -6 : 1 - dagNummer);
}

/** Het aantal HELE dagen in de periode, met de tot-datum meegeteld. */
export function dagenInPeriode(periode: Periode): number {
  const van = parse(periode.from);
  const tot = parse(periode.to);
  // Afronden vangt de zomertijdsprong op: dan is het verschil geen rond aantal etmalen.
  return Math.round((tot.getTime() - van.getTime()) / 86_400_000) + 1;
}

/**
 * De korrel die bij de lengte van de periode hoort.
 *
 * De keuze is zichtbaar op het scherm, want een staaf zegt iets heel anders zodra hij een week in
 * plaats van een dag beslaat.
 */
export function korrelVoor(periode: Periode): Korrel {
  const dagen = dagenInPeriode(periode);
  if (dagen <= KORREL_GRENS_PER_DAG) return 'day';
  if (dagen <= KORREL_GRENS_PER_WEEK) return 'week';
  return 'month';
}

const LEGE_STAP: StapInTijdvak = { minuten: null, behandelingen: null, gemiddeldePerStuk: null };

/** Het begin van het tijdvak waarin deze dag valt. */
function beginVanTijdvak(dag: Date, korrel: Korrel): Date {
  if (korrel === 'day') return dag;
  if (korrel === 'week') return maandagVan(dag);
  return new Date(dag.getFullYear(), dag.getMonth(), 1);
}

/** Het eerstvolgende tijdvak na dit begin. */
function volgendTijdvak(begin: Date, korrel: Korrel): Date {
  if (korrel === 'day') return verschuif(begin, 1);
  if (korrel === 'week') return verschuif(begin, 7);
  return new Date(begin.getFullYear(), begin.getMonth() + 1, 1);
}

/** De sleutel die de bron aan dit tijdvak geeft. */
function labelVoor(begin: Date, korrel: Korrel): string {
  if (korrel === 'month') return alsDatum(begin).slice(0, 7);
  return alsDatum(begin);
}

/** Het begin van een tijdvak, teruggerekend uit de sleutel die de bron meegaf. */
function beginUitLabel(label: string, korrel: Korrel): Date {
  return parse(korrel === 'month' ? `${label}-01` : label);
}

/** Minuten gedeeld door behandelingen, en niets zodra er niets te delen valt. */
function gemiddelde(minuten: number | null, behandelingen: number | null): number | null {
  if (minuten === null || behandelingen === null || behandelingen <= 0) return null;
  return minuten / behandelingen;
}

/**
 * Eén stap uit een tijdvak van de bron. Een stap die er niet staat is niet gemeten, niet nul.
 *
 * ÁLLE regels van die stap tellen op, en niet alleen de eerste. Dit stond hier als een `.find()`,
 * en dat is een bevinding uit de epic-brede review van 30 augustus 2026: op dezelfde invoer gaf de tabel 120 minuten en 8,0 per stuk terwijl
 * de grafiek 60 en 12,0 tekende. Vandaag valt het verschil niet op — de bron bouwt `handlings[]`
 * door de twee proceskodes te filteren en elke kode één-op-één op een handelingsnaam af te beelden,
 * dus staat er per stap precies één regel — maar het contract bindt de reeks aan de totalen ("de som
 * over álle tijdvakken is exact gelijk aan `data[].minutes` van die stap"), en een `.find()` die een
 * tweede regel laat liggen breekt die belofte stil. Sinds story 33.1 staan de tabel en de grafiek
 * bovendien op precies dezelfde gekozen stap naast elkaar, dus zou dat verschil zichtbaar op het
 * scherm belanden als twee getallen die elkaar tegenspreken.
 *
 * EEN REGEL ZONDER MINUUT WORDT IN ZIJN GEHEEL OVERGESLAGEN, stuks en al — en `stapVan` in
 * `tabel.ts` doet dat sinds de tweede reviewronde van 30 augustus 2026 net zo. Dáár telden de
 * `tradeItems` van zo'n regel nog wel mee, en dat is precies het verschil dat deze toelichting
 * eerder al beloofde weg te nemen terwijl het er nog stond: op `{ minutes: null, tradeItems: 3 }`
 * naast `{ minutes: 60, tradeItems: 5 }` gaf de tabel 8 behandelingen en 7,5 minuut per stuk waar
 * de grafiek 5 en 12,0 tekende. Het contract bindt beide kanten aan overslaan: zo'n regel hoort er
 * niet te zijn ("een stap waarin diegene geen minuut droeg krijgt GEEN regel, ook niet één met
 * nul"), en `tradeItems` telt alleen de stukken waaraan werkelijk gemeten is — een noemer zonder
 * teller is geen behandeling.
 */
function stapUit(bucket: HandlingTimeBucket | undefined, stap: string): StapInTijdvak {
  let minuten: number | null = null;
  let behandelingen: number | null = null;

  for (const eigen of bucket?.handlings ?? []) {
    // Een regel ZONDER minuten telt niet mee en wist de vorige niet: leeg is geen nul, en twee lege
    // regels blijven samen leeg.
    if (eigen.handling !== stap || eigen.minutes === null) continue;
    minuten = (minuten ?? 0) + eigen.minutes;
    behandelingen = (behandelingen ?? 0) + eigen.tradeItems;
  }

  if (minuten === null) return LEGE_STAP;
  return { minuten, behandelingen, gemiddeldePerStuk: gemiddelde(minuten, behandelingen) };
}

function bouwTijdvak(
  begin: Date,
  korrel: Korrel,
  periode: Periode,
  bucket: HandlingTimeBucket | undefined,
  // Het label ZOALS DE BRON HET GAF, wanneer dat er is. Reviewbevinding 27-08-2026: hieronder
  // werd het label opnieuw berekend uit de begindatum, en dat is alleen hetzelfde zolang bron en
  // scherm het over de korrel eens zijn. Stuurt de bron ooit een maandlabel terwijl om dagen
  // gevraagd is, dan levert de herberekening een ánder label dan waarop hierboven op dubbelen
  // is gecontroleerd — en dan staan er twee tijdvakken met dezelfde naam in de lijst.
  bronLabel?: string,
): Tijdvak {
  const eindeVolgend = volgendTijdvak(begin, korrel);
  const tot = verschuif(eindeVolgend, -1);
  const vastleggen = stapUit(bucket, STAP_VASTLEGGEN);
  const valideren = stapUit(bucket, STAP_VALIDEREN);

  // Optellen zonder van "niets" een nul te maken: twee lege stappen blijven samen leeg.
  const totaleMinuten = vastleggen.minuten === null
    ? valideren.minuten
    : vastleggen.minuten + (valideren.minuten ?? 0);

  return {
    label: bronLabel ?? labelVoor(begin, korrel),
    van: alsDatum(begin),
    tot: alsDatum(tot),
    korrel,
    // Een tijdvak dat geen enkele minuut draagt is niet gemeten — ook niet als de bron er een
    // regel voor stuurde. Anders zou een lege regel alsnog een staaf van nul opleveren.
    gemeten: totaleMinuten !== null,
    rand: begin < parse(periode.from) || tot > parse(periode.to),
    totaleMinuten,
    vastleggen,
    valideren,
  };
}

/**
 * De volledige reeks tijdvakken over de periode, met de metingen van de bron erin gehangen.
 *
 * Elk tijdvak van de periode komt hierin voor, óók de tijdvakken waarin niets gemeten is. Dat is
 * het hele punt: die tijdvakken bestaan wél, ze dragen alleen niets.
 */
export function bouwReeks(
  periode: Periode,
  buckets: HandlingTimeBucket[] | undefined,
  korrel: Korrel = korrelVoor(periode),
): Tijdvak[] {
  const perLabel = new Map<string, HandlingTimeBucket>();
  for (const bucket of buckets ?? []) perLabel.set(bucket.label, bucket);

  const tijdvakken: Tijdvak[] = [];
  const gezien = new Set<string>();

  const laatste = parse(periode.to);
  for (let begin = beginVanTijdvak(parse(periode.from), korrel); begin <= laatste; begin = volgendTijdvak(begin, korrel)) {
    const label = labelVoor(begin, korrel);
    gezien.add(label);
    tijdvakken.push(bouwTijdvak(begin, korrel, periode, perLabel.get(label)));
  }

  // Een tijdvak dat de bron levert maar dat buiten dit rooster valt, WEGLATEN zou de ergste
  // uitkomst zijn: dan verdwijnt gemeten werk zonder een spoor. Het komt er dus gewoon bij, op zijn
  // eigen plek in de tijd.
  for (const bucket of buckets ?? []) {
    if (gezien.has(bucket.label)) continue;
    gezien.add(bucket.label);
    tijdvakken.push(bouwTijdvak(beginUitLabel(bucket.label, korrel), korrel, periode, bucket, bucket.label));
  }

  return tijdvakken.sort((a, b) => (a.van < b.van ? -1 : a.van > b.van ? 1 : 0));
}
